import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  SimpleGrid,
  Spinner,
  Center,
  HStack,
  Button,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { aboutUsService, formatAboutUsData } from "../services/aboutUsService";
import ServiceDetailsHeader from "../components/Common/ImageHeader";
import { FaFacebook, FaTwitter, FaLinkedin } from "react-icons/fa";

// Helper function to format URLs
const formatUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

const Leaders = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

  const handleBreadcrumbClick = (href) => {
    if (href && href !== "#") {
      navigate(href);
    }
  };

  // Helper function to ensure URLs have proper protocol
  const formatUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  useEffect(() => {
    const fetchLeadersData = async () => {
      try {
        const response = await aboutUsService.getAboutUsData();
        if (response.success) {
          const formattedData = formatAboutUsData(response.data);
          setData(formattedData);
        } else {
          setError(response.error);
        }
      } catch (err) {
        console.error('Error fetching leaders data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLeadersData();
  }, []);

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" color="#000B8C" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center h="100vh">
        <Text color="red.500">Error loading leaders: {error}</Text>
      </Center>
    );
  }

  const allLeaders = data?.leaderProfile?.map((leader, index) => {
    // Handle image URL - if it already has full URL, use it directly, otherwise prepend baseUrl
    let imageUrl = null;
    if (leader?.image?.url) {
      if (leader.image.url.startsWith('http://') || leader.image.url.startsWith('https://')) {
        imageUrl = leader.image.url; // Already has full URL
      } else {
        imageUrl = `${baseUrl}${leader.image.url}`; // Need to prepend baseUrl
      }
    }
    
    return {
      name: leader?.name,
      position: leader?.designation,
      image: imageUrl,
      bio: leader?.bio,
      profileLink: leader?.profileLink,
      // Add social media links matching your CMS field names with proper formatting
      facebook: formatUrl(leader?.facebookLink),
      twitter: formatUrl(leader?.twiterLink), // Note: using 'twiterLink' as shown in your CMS
      linkedin: formatUrl(leader?.linkedinLink),
    };
  }) || [];

  return (
    <>
      <ServiceDetailsHeader
        title="Our Leaders"
        subtitle="Meet our dedicated leadership team driving innovation and excellence in business automation."
        backgroundImage="/png/eGovernment-Solution.png"
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "About Us", href: "/about-us" },
          { label: "Our Leaders", href: "#", isCurrentPage: true },
        ]}
        onBreadcrumbClick={handleBreadcrumbClick}
      />
      
      <Box minH="100vh" py={{ base: "60px", md: "80px" }}>
        <Container maxW="1200px">
          {/* Leaders Grid */}
          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing={{ base: 6, md: 8 }}
            mx="auto"
            justifyItems="center"
          >
          {allLeaders.map((leader, index) => (
            <Box
              key={index}
              bg="white"
              rounded="2xl"
              overflow="hidden"
              shadow="lg"
              position="relative"
              maxW="380px"
              w="100%"
              h={{ base: "480px", sm: "520px", md: "540px" }}
              display="flex"
              flexDirection="column"
              transition="transform 0.3s ease"
              _hover={{ 
                transform: "translateY(-5px)",
                shadow: "xl"
              }}
            >
              {/* Leader Image */}
              <Box position="relative" h={{ base: "320px", sm: "360px", md: "400px" }}>
                {leader.image ? (
                  <Image
                    src={leader.image}
                    alt={leader.name}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    onError={(e) => {
                      console.error('Leader image failed to load:', leader.image);
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <Box
                    w="100%"
                    h="100%"
                    bg="gray.200"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="lg" color="gray.500">No Image</Text>
                  </Box>
                )}

                {/* View Profile Button */}
                {leader.profileLink && (
                  <Box
                    as="a"
                    href={leader.profileLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    position="absolute"
                    left="0"
                    top="70%"
                    transform="translateY(-50%)"
                    bg="blue.700"
                    color="white"
                    roundedRight="lg"
                    cursor="pointer"
                    transition="all 0.3s ease"
                    _hover={{ bg: "blue.800", textDecoration: "none" }}
                    display={{ base: "none", sm: "block" }}
                  >
                    <Text
                      py={{ base: 6, sm: 8, md: 10 }}
                      px={2}
                      fontSize={{ base: "xs", sm: "sm" }}
                      fontWeight="semibold"
                      transform="rotate(-90deg)"
                      whiteSpace="nowrap"
                      letterSpacing="wider"
                    >
                      View Profile
                    </Text>
                  </Box>
                )}
              </Box>

              {/* Leader Info */}
              <Box p={{ base: 1, sm: 2 }} textAlign="center" flex="1" display="flex" flexDirection="column" justifyContent="space-between">
                <Box>
                  <Heading 
                    as="h3" 
                    size={{ base: "sm", sm: "md" }} 
                    color="blue.700" 
                    mb={1} 
                    fontWeight="bold"
                    lineHeight="1"
                    minH={{ base: "30px", sm: "36px" }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {leader.name}
                  </Heading>
                  <Text 
                    color="#000B8C" 
                    fontSize={{ base: "sm", sm: "md" }} 
                    mb={1} 
                    fontWeight="medium"
                    lineHeight="1"
                    minH={{ base: "20px", sm: "24px" }}
                  >
                    {leader.position}
                  </Text>
                </Box>

                {/* Social Media Icons */}
                <HStack justify="center" spacing={4}>
                  {leader.facebook && (
                    <Box
                      as="a"
                      href={leader.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      p={2}
                      rounded="md"
                      bg="#000B8C"
                      color="white"
                      transition="all 0.3s ease"
                      _hover={{ bg: "blue.700", transform: "scale(1.1)" }}
                      cursor="pointer"
                    >
                      <FaFacebook size={16} />
                    </Box>
                  )}
                  {leader.twitter && (
                    <Box
                      as="a"
                      href={leader.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      p={2}
                      rounded="md"
                      bg="blue.400"
                      color="white"
                      transition="all 0.3s ease"
                      _hover={{ bg: "#000B8C", transform: "scale(1.1)" }}
                      cursor="pointer"
                    >
                      <FaTwitter size={16} />
                    </Box>
                  )}
                  {leader.linkedin && (
                    <Box
                      as="a"
                      href={leader.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      p={2}
                      rounded="md"
                      bg="blue.700"
                      color="white"
                      transition="all 0.3s ease"
                      _hover={{ bg: "blue.800", transform: "scale(1.1)" }}
                      cursor="pointer"
                    >
                      <FaLinkedin size={16} />
                    </Box>
                  )}
                  {/* Show placeholder icons if no social media links but maintain layout */}
                  {!leader.facebook && !leader.twitter && !leader.linkedin && (
                    <>
                      <Box
                        p={2}
                        rounded="md"
                        bg="gray.300"
                        color="white"
                      >
                        <FaFacebook size={16} />
                      </Box>
                      <Box
                        p={2}
                        rounded="md"
                        bg="gray.300"
                        color="white"
                      >
                        <FaTwitter size={16} />
                      </Box>
                      <Box
                        p={2}
                        rounded="md"
                        bg="gray.300"
                        color="white"
                      >
                        <FaLinkedin size={16} />
                      </Box>
                    </>
                  )}
                </HStack>
              </Box>
            </Box>
          ))}
        </SimpleGrid>

        {/* Empty State */}
        {allLeaders.length === 0 && (
          <Center mt={12}>
            <Text fontSize="lg" color="gray.500">No leaders found.</Text>
          </Center>
        )}
        </Container>
      </Box>
    </>
  );
};

export default Leaders;
